import { useEventManagerContext } from "../context/eventContext";

export default function useEventPDF() {
  const {
    selectedEvent,
    selectedSchoolyear,
    shifts = [],
    jobs = [],
    angebote = [],
  } = useEventManagerContext();

  /**
   * =========================================================
   * ✅ SHARED HELPERS
   * =========================================================
   */
  const loadLogo = async () => {
    const img = new Image();
    img.src = "/static/images/ksb/ksb_logo.png";
    await new Promise((resolve) => (img.onload = resolve));
    return img;
  };

  const renderHeaderFactory = (doc, img, margin, pageWidth) => {
    return () => {
      const imgWidth = img.width;
      const imgHeight = img.height;

      const targetWidth = pageWidth - margin.left - margin.right;
      const targetHeight = (imgHeight / imgWidth) * targetWidth;

      doc.addImage(
        img,
        "PNG",
        margin.left - 10,
        10,
        targetWidth + 22,
        targetHeight + 2,
      );

      const titleY = 10 + targetHeight + 6;

      doc.setFont(undefined, "bold");
      doc.setFontSize(14);

      doc.text(
        `${selectedSchoolyear?.label} ${selectedSchoolyear?.label || ""}`,
        margin.left,
        titleY,
      );

      doc.line(margin.left, titleY + 3, pageWidth - margin.right, titleY + 3);

      return titleY + 8; // ✅ important vertical spacing
    };
  };

  const getLastName = (name) => {
    if (!name) return "";
    const parts = name.trim().split(" ");
    return parts[parts.length - 1];
  };

  /**
   * =========================================================
   * ✅ SHIFT → STRUCTURE PDF (IMPROVED)
   * =========================================================
   */
  const generatePDF = async () => {
    try {
      const { jsPDF } = await import("jspdf");

      const doc = new jsPDF();

      const margin = { top: 20, bottom: 20, left: 15, right: 15 };
      const pageHeight = doc.internal.pageSize.height;
      const pageWidth = doc.internal.pageSize.width;

      const lineSpacing = 5;

      const img = await loadLogo();
      const renderHeader = renderHeaderFactory(doc, img, margin, pageWidth);

      /**
       * ✅ MULTI COLUMN (restored behavior)
       */
      const renderMultiColumnList = (items, startX, startY, columnCount) => {
        const columnWidth =
          (pageWidth - margin.left - margin.right) / columnCount;

        const rows = Math.ceil(items.length / columnCount);

        let y = startY;

        for (let row = 0; row < rows; row++) {
          for (let col = 0; col < columnCount; col++) {
            const index = col * rows + row;
            if (index >= items.length) continue;

            doc.text(`• ${items[index]}`, startX + col * columnWidth + 2, y);
          }
          y += lineSpacing;
        }

        return y;
      };

      /**
       * ✅ HEIGHT CALCULATION (critical fix)
       */
      const getListHeight = (items, columnCount = 1) => {
        if (items.length <= 5 || columnCount === 1) {
          return items.length * lineSpacing;
        }

        const rows = Math.ceil(items.length / columnCount);
        return rows * lineSpacing;
      };

      shifts.forEach((shift, index) => {
        if (index !== 0) doc.addPage();

        let y = renderHeader();

        const shiftJobs = jobs.filter((j) => j.shiftId === shift.id);

        doc.setFont(undefined, "bold");
        doc.setFontSize(12);

        const title = shift.time
          ? `${shift.label} (${shift.time})`
          : shift.label;

        doc.text(title, margin.left, y);
        y += lineSpacing + 2;

        shiftJobs.forEach((job) => {
          const jobAngebote = angebote.filter((a) => a.jobId === job.id);

          const leitung = jobAngebote
            .filter((a) => a.type === "leitung")
            .map((a) => a.reservedBy?.name || "frei");

          const team = jobAngebote
            .filter((a) => a.type === "team")
            .map((a) => a.reservedBy?.name || "frei");

          /**
           * ✅ estimate total height BEFORE rendering
           */
          let estimatedHeight = 10;

          if (leitung.length > 0) {
            estimatedHeight += lineSpacing + getListHeight(leitung, 1) + 4;
          }

          if (team.length > 0) {
            const columnCount = team.length > 12 ? 4 : team.length > 8 ? 3 : 1;

            estimatedHeight +=
              lineSpacing + getListHeight(team, columnCount) + 4;
          }

          // ✅ prevent overflow EARLY
          if (y + estimatedHeight > pageHeight - margin.bottom) {
            doc.addPage();
            y = renderHeader();
          }

          y += 3;

          doc.setFont(undefined, "bold");
          doc.setFontSize(11);
          doc.text(job.title, margin.left + 2, y);
          y += lineSpacing;

          doc.setFont(undefined, "normal");
          doc.setFontSize(9);

          /**
           * ✅ LEITUNG (always simple, like before)
           */
          if (leitung.length > 0) {
            doc.setFont(undefined, "bold");
            doc.text("Lehrperson(en)", margin.left + 4, y);
            y += lineSpacing;

            doc.setFont(undefined, "normal");

            leitung.forEach((name) => {
              doc.text(`• ${name}`, margin.left + 6, y);
              y += lineSpacing;
            });

            y += 2;
          }

          /**
           * ✅ TEAM (smart layout)
           */
          if (team.length > 0) {
            doc.setFont(undefined, "bold");
            doc.text("Lernende", margin.left + 4, y);
            y += lineSpacing;

            doc.setFont(undefined, "normal");

            const columnCount = team.length > 12 ? 4 : team.length > 8 ? 3 : 1;

            if (columnCount === 1) {
              team.forEach((name) => {
                doc.text(`• ${name}`, margin.left + 6, y);
                y += lineSpacing;
              });
            } else {
              y = renderMultiColumnList(team, margin.left + 4, y, columnCount);
            }

            y += 3;
          }
        });

        /**
         * ✅ FOOTER
         */
        doc.setFontSize(8);
        doc.text(
          `Seite ${doc.getCurrentPageInfo().pageNumber}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: "center" },
        );
      });

      doc.save(
        `${selectedSchoolyear?.label}_${selectedEvent?.label}_Einsatzliste.pdf`,
      );
    } catch (err) {
      console.error("❌ PDF ERROR:", err);
    }
  };

  /**
   * =========================================================
   * ✅ BUILD PARTICIPANT MAP
   * =========================================================
   */
  const buildParticipants = () => {
    const map = {};

    angebote.forEach((angebot) => {
      if (!angebot.reservedBy) return;

      const job = jobs.find((j) => j.id === angebot.jobId);
      const shift = shifts.find((s) => s.id === job?.shiftId);
      if (!job || !shift) return;

      if (!map[angebot.reservedBy.id]) {
        map[angebot.reservedBy.id] = {
          name: angebot.reservedBy.name,
          entries: [],
        };
      }

      map[angebot.reservedBy.id].entries.push({
        text: `${shift.label} (${shift.time}) – ${job.title}`,
        type: angebot.type,
      });
    });

    return map;
  };

  /**
   * =========================================================
   * ✅ PARTICIPANT PDF (FINAL VERSION)
   * =========================================================
   */
  const generateParticipantPDF = async () => {
    const { jsPDF } = await import("jspdf");

    const doc = new jsPDF();

    const margin = { left: 15, right: 15 };
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const maxWidth = pageWidth - margin.left - margin.right;

    const lineSpacing = 4.5;
    const nameColumnWidth = 36; // ✅ reduced gap

    const img = await loadLogo();
    const renderHeader = renderHeaderFactory(doc, img, margin, pageWidth);

    const setBaseFont = () => {
      doc.setFont(undefined, "normal");
      doc.setFontSize(9);
    };

    let y = renderHeader();
    setBaseFont();

    const participants = buildParticipants();

    const lehrpersonen = [];
    const lernende = [];

    Object.values(participants).forEach((p) => {
      if (p.entries.some((e) => e.type === "leitung")) {
        lehrpersonen.push(p);
      } else {
        lernende.push(p);
      }
    });

    /**
     * ✅ PAGE BREAK HANDLER
     */
    const ensureSpace = (space = 10) => {
      if (y + space > pageHeight - 15) {
        doc.addPage();
        y = renderHeader();
        setBaseFont();
      }
    };

    /**
     * =========================================================
     * ✅ UNIFIED RENDER (TEACHERS + STUDENTS)
     * =========================================================
     */
    const renderList = (title, list) => {
      doc.setFont(undefined, "bold");
      doc.setFontSize(11);
      doc.text(title, margin.left, y);
      y += 6;

      setBaseFont();

      list
        .sort((a, b) => getLastName(a.name).localeCompare(getLastName(b.name)))
        .forEach((user) => {
          ensureSpace(10);

          // ✅ NAME
          doc.setFont(undefined, "bold");
          doc.text(user.name, margin.left, y);

          setBaseFont();

          let entryY = y;

          user.entries.forEach((entry) => {
            ensureSpace(6);

            const text = entry.text;

            // ✅ split text correctly
            const lines = doc.splitTextToSize(
              text,
              maxWidth - nameColumnWidth - 3, // space for bullet
            );

            // ✅ render first line WITH bullet
            doc.text(`• ${lines[0]}`, margin.left + nameColumnWidth, entryY);

            // ✅ render wrapped lines aligned WITHOUT extra bullet
            for (let i = 1; i < lines.length; i++) {
              doc.text(
                lines[i],
                margin.left + nameColumnWidth + 1.75, // ✅ indent continuation
                entryY + i * lineSpacing,
              );
            }

            // ✅ correct vertical spacing
            entryY += lines.length * lineSpacing;
          });

          y = entryY + 2;
        });
    };

    /**
     * =========================================================
     * ✅ RENDER TEACHERS
     * =========================================================
     */
    renderList("Lehrpersonen", lehrpersonen);

    /**
     * =========================================================
     * ✅ NEW PAGE FOR STUDENTS
     * =========================================================
     */
    doc.addPage();
    y = renderHeader();
    setBaseFont();

    renderList("Lernende", lernende);

    doc.save(
      `${selectedSchoolyear?.label}_${selectedEvent?.label}_Personenliste.pdf`,
    );
  };

  /**
   * =========================================================
   * ✅ EXPORT API
   * =========================================================
   */
  return {
    generatePDF,
    generateParticipantPDF,
  };
}
